<script setup lang="ts">
    import type { Asset, AssetWidgetStyle } from '~/@types/cms';
    import { Swiper, SwiperSlide } from 'swiper/vue';
    import 'swiper/css';

    declare interface ProductImageProps {
        images: Asset[];
        style?: AssetWidgetStyle;
        caption?: string;
    }
    const props = defineProps<ProductImageProps>();
    const { setMainSwiper, swipeLeft, swipeRight, isBeginning, isEnd, ssrIsActive } = useGallery();

    const isFullBleed = computed(() => props.style?.size === 'fullbleed');

    const imageSizes = computed(() => {
        return isFullBleed ? 'sm:768px md:1024px lg:1440px xl:1920px' : 'sm:640px md:768px lg:1024px xl:1440px 2xl:1920px';
    });
</script>

<template>
    <div class="mol-image-gallery relative mb-8 overflow-hidden md:mb-10">
        <atm-grid
            :no-margin="isFullBleed"
            :full-width="isFullBleed">
            <div
                :class="[
                    'col-span-2 md:col-span-12',
                    {
                        'lg:col-start-3 lg:col-end-11': style?.size === 'text',
                    },
                ]">
                <nuxt-error-boundary @error="() => {}">
                    <swiper
                        loop
                        :space-between="16"
                        @swiper="setMainSwiper"
                        class="max-lg:!overflow-clip max-lg:!overflow-x-visible">
                        <swiper-slide
                            v-for="(image, index) in images"
                            v-slot="{ isActive }">
                            <lazy-atm-asset
                                cover
                                :data="image"
                                :sizes="imageSizes"
                                :showCaption="ssrIsActive(isActive, index)"
                                :full-bleed="isFullBleed" />
                        </swiper-slide>
                    </swiper>
                </nuxt-error-boundary>

                <lazy-atm-caption
                    v-if="caption"
                    :caption="caption"
                    :full-bleed="isFullBleed" />
            </div>
        </atm-grid>

        <lazy-mol-swiper-navigation
            v-if="images.length > 1"
            :swipe-left="swipeLeft"
            :swipe-right="swipeRight"
            :is-beginning="isBeginning"
            :is-end="isEnd" />
    </div>
</template>
